import { DialogContainer, IconButton, Skeleton, Text } from '@components'
import { ArrowLeft, ArrowRight } from 'lucide-solid'
import {
	type Accessor,
	type JSX,
	type Setter,
	Show,
	createEffect,
	createSignal,
	onCleanup,
} from 'solid-js'
import { css } from 'styled-system/css'
import { Box } from 'styled-system/jsx'

export const ZoomableImage = (props: {
	src?: string
	title?: string
	children?: JSX.Element
	isOpen?: Accessor<boolean> | (() => boolean)
	setIsOpen?: Setter<boolean> | ((value: boolean) => void)
	next?: () => void
	prev?: () => void
}) => {
	const [localIsOpen, setLocalIsOpen] = createSignal(false)
	const [isLoaded, setIsLoaded] = createSignal(false)

	const isOpen = props.isOpen ? props.isOpen : localIsOpen
	const setIsOpen = props.setIsOpen ? props.setIsOpen : setLocalIsOpen

	createEffect(() => {
		if (!isOpen() || (!props.prev && !props.next)) return

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'ArrowLeft' && props.prev) {
				e.preventDefault()
				props.prev()
			} else if (e.key === 'ArrowRight' && props.next) {
				e.preventDefault()
				props.next()
			}
		}

		window.addEventListener('keydown', handleKeyDown)
		onCleanup(() => window.removeEventListener('keydown', handleKeyDown))
	})

	return (
		<>
			<DialogContainer
				padding="0"
				isOpen={isOpen()}
				setIsOpen={setIsOpen}
			>
				<img
					alt={props.src}
					class={css({
						maxWidth: '100vw',
						maxHeight: '100vh',
						objectFit: 'cover',
						objectPosition: 'center',
					})}
					src={props.src}
					onload={() => setIsLoaded(true)}
				/>
				<Show when={props?.title}>
					<Text
						position="absolute"
						zIndex={1}
						size="xs"
						bottom={0}
						p={1}
						bg="bg.canvas"
						borderRadius="xs"
					>
						{props?.title}
					</Text>
				</Show>
				<Show when={props.prev && props.next}>
					<Box
						class={css({
							position: 'absolute',
							top: '50%',
							transform: 'translateY(-50%)',
							width: '100%',
							display: 'flex',
							justifyContent: 'space-between',
						})}
					>
						<IconButton
							onClick={props.prev}
							colorPalette="bg"
							variant="solid"
							size="xs"
							h="6"
							w="6"
						>
							<ArrowLeft />
						</IconButton>
						<IconButton
							onClick={props.next}
							colorPalette="bg"
							variant="solid"
							size="xs"
							h="6"
							w="6"
						>
							<ArrowRight />
						</IconButton>
					</Box>
				</Show>
			</DialogContainer>
			<Skeleton isLoaded={isLoaded()}>
				<Box
					class={css({
						position: 'relative',
						height: '100%',
						display: 'grid',
						justifyContent: 'stretch',
						alignItems: 'center',
						cursor: 'zoom-in',
					})}
					onClick={() => setIsOpen(true)}
				>
					<Show
						when={props.children}
						fallback={
							<div
								class={css({
									position: 'absolute',
									bgPosition: 'center center',
									backgroundSize: 'cover',
									height: '100%',
									width: '100%',
								})}
								style={{
									'background-image': `url(${props.src})`,
								}}
							/>
						}
					>
						{props.children}
					</Show>
					<Show when={props?.title}>
						<Text
							position="absolute"
							zIndex={1}
							size="xs"
							bottom={0}
							p={1}
							bg="bg.canvas"
							borderRadius="xs"
						>
							{props?.title}
						</Text>
					</Show>
				</Box>
			</Skeleton>
		</>
	)
}
